/**
 * Builder Redesign page (v4 — 2-panel layout).
 *
 * Route: `/builder/:id`
 *
 * Desktop (≥ 1024px): BuilderTopbar + BuilderShell (left editor, right preview).
 * Mobile (< 1024px): MobileReview (read-only preview + action bar).
 *
 * Topbar center: Content / Design pill tabs switch the left panel.
 * - Content → ContentEditor (section forms)
 * - Design → SettingsPanel (theme, typography, page, layout, templates)
 *
 * Hooks: useBuilderSettings, useZoomPan, useExportActions, useVersionHistory, useAutoSave.
 */
import type { DragEndEvent } from "@dnd-kit/core";
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { restrictToParentElement } from "@dnd-kit/modifiers";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { t } from "@lingui/macro";
import { createId } from "@paralleldrive/cuid2";
import {
  BriefcaseIcon,
  CertificateIcon,
  GraduationCapIcon,
  IdentificationCardIcon,
  SparkleIcon,
  StackIcon,
  TextAaIcon,
  TranslateIcon,
  TrashIcon,
  UploadSimpleIcon,
} from "@phosphor-icons/react";
import type { ResumeDto } from "@reactive-resume/dto";
import { useBreakpoint } from "@reactive-resume/hooks";
import { useTheme } from "@reactive-resume/hooks";
import type { Section, SectionKey } from "@reactive-resume/schema";
import {
  defaultCustomSection,
  defaultExperience,
  defaultProfile,
  defaultSkill,
} from "@reactive-resume/schema";
import {
  Button,
  Input,
  Popover,
  PopoverContent,
  PopoverTrigger,
  RichInput,
} from "@reactive-resume/ui";
import get from "lodash.get";
import React, { lazy, Suspense, useCallback, useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import type { LoaderFunction } from "react-router";
import { redirect } from "react-router";
import { z } from "zod";

import { BRANDING } from "@/client/constants/branding";
import { useMe } from "@/client/hooks/use-me";
import { useToast } from "@/client/hooks/use-toast";
import { queryClient } from "@/client/libs/query-client";
import { findResumeById } from "@/client/services/resume";
import { useUploadImage } from "@/client/services/storage";
import { useBuilderStore } from "@/client/stores/builder";
import { useResumeStore, useTemporalResumeStore } from "@/client/stores/resume";

import { buildDateString, parseDateString } from "./date-adapter";
import { useAutoSave } from "./hooks/use-auto-save";
import { useBlockedSaveNavigation } from "./hooks/use-blocked-save-navigation";
import { useBuilderSettings } from "./hooks/use-builder-settings";
import { useExportActions } from "./hooks/use-export-actions";
import { useZoomPan } from "./hooks/use-zoom-pan";
import { BuilderShell } from "./layout/builder-shell";
import type { BuilderMode } from "./layout/builder-topbar";
import { BuilderTopbar, defaultBuilderMenuItems } from "./layout/builder-topbar";
import { MobileReview } from "./layout/mobile-review";
import { SaveBadge } from "./layout/save-badge";
import { RightPanel } from "./panels/right-panel";
import { AddSectionCard } from "./sections/add-section-card";
import { BasicsForm, type BasicsValue, type SocialLink } from "./sections/basics-form";
import { type ExperienceEntry, ExperienceForm } from "./sections/experience-form";
import { SectionBlock } from "./sections/section-block";
import { getSectionFieldMap, SECTION_FIELD_KEYS } from "./sections/section-field-map";
import type { Skill as DesignSkill } from "./sections/skills-form";
import { SkillsForm } from "./sections/skills-form";
import { SummaryForm } from "./sections/summary-form";

// Lazy-imported modals & settings
const TemplateSwitcherModal = lazy(() =>
  import("./modals/template-switcher-modal").then((m) => ({ default: m.TemplateSwitcherModal })),
);
const SettingsPanel = lazy(() =>
  import("./settings/settings-panel").then((m) => ({ default: m.SettingsPanel })),
);

const ARTBOARD_BASE =
  (import.meta.env.VITE_ARTBOARD_URL as string | undefined)?.replace(/\/$/, "") ?? "";
// eslint-disable-next-line lingui/no-unlocalized-strings
const ARTBOARD_SRC = `${ARTBOARD_BASE}/artboard/builder`;
const ARTBOARD_TARGET_ORIGIN = ARTBOARD_BASE || window.location.origin;

// ──────────────────────────────────────────────────────────
// Section icons
// ──────────────────────────────────────────────────────────

const SECTION_ICONS: Record<string, React.JSX.Element> = {
  basics: <IdentificationCardIcon size={18} />,
  summary: <TextAaIcon size={18} />,
  experience: <BriefcaseIcon size={18} />,
  education: <GraduationCapIcon size={18} />,
  skills: <SparkleIcon size={18} />,
  projects: <StackIcon size={18} />,
  certifications: <CertificateIcon size={18} />,
  languages: <TranslateIcon size={18} />,
};

// ──────────────────────────────────────────────────────────
// Skill level adapter: schema uses 0-5, design uses 0-3 index
// ──────────────────────────────────────────────────────────
const LEVEL_SCHEMA_TO_DESIGN = [0, 0, 1, 2, 2, 3] as const;
const LEVEL_DESIGN_TO_SCHEMA = [0, 2, 3, 5] as const;

// ──────────────────────────────────────────────────────────
// Field mapping per section type for ExperienceForm
// ──────────────────────────────────────────────────────────
// FieldMap, SECTION_FIELD_KEYS, isCustomSectionKey, and getSectionFieldMap now live in
// ./sections/section-field-map (so the custom-section mapping is unit-testable). Custom
// sections are handled there — without it the entry form rendered null for them.

const SKILL_SECTIONS = new Set(["skills", "interests", "languages"]);
const ENTRY_SECTIONS = new Set(Object.keys(SECTION_FIELD_KEYS));

// eslint-disable-next-line lingui/no-unlocalized-strings
const PLATFORM_OPTIONS: { value: string; label: string }[] = [
  { value: "github", label: "GitHub" }, // eslint-disable-line lingui/no-unlocalized-strings
  { value: "linkedin", label: "LinkedIn" }, // eslint-disable-line lingui/no-unlocalized-strings
  { value: "twitter", label: "Twitter" }, // eslint-disable-line lingui/no-unlocalized-strings
  { value: "facebook", label: "Facebook" }, // eslint-disable-line lingui/no-unlocalized-strings
  { value: "instagram", label: "Instagram" }, // eslint-disable-line lingui/no-unlocalized-strings
  { value: "youtube", label: "YouTube" }, // eslint-disable-line lingui/no-unlocalized-strings
  { value: "dribbble", label: "Dribbble" }, // eslint-disable-line lingui/no-unlocalized-strings
  { value: "behance", label: "Behance" }, // eslint-disable-line lingui/no-unlocalized-strings
  { value: "medium", label: "Medium" }, // eslint-disable-line lingui/no-unlocalized-strings
  { value: "stackoverflow", label: "Stack Overflow" }, // eslint-disable-line lingui/no-unlocalized-strings
];

// ──────────────────────────────────────────────────────────
// ArtboardFrame
// ──────────────────────────────────────────────────────────

const ArtboardFrame = ({ fit }: { fit?: "width" }) => {
  const frameRef = useBuilderStore((state) => state.frame.ref);
  const setFrameRef = useBuilderStore((state) => state.frame.setRef);
  const resumeData = useResumeStore((state) => state.resume.data);
  const { isDarkMode } = useTheme();

  const syncResumeToArtboard = useCallback(() => {
    if (!frameRef?.contentWindow) return;
    frameRef.contentWindow.postMessage(
      { type: "SET_RESUME", payload: resumeData },
      ARTBOARD_TARGET_ORIGIN,
    );
  }, [frameRef, resumeData]);

  useEffect(() => {
    if (!frameRef) return;
    const onLoad = () => {
      let attempts = 0;
      const tick = () => {
        syncResumeToArtboard();
        attempts += 1;
        if (attempts < 12) setTimeout(tick, 250);
      };
      tick();
      setTimeout(() => {
        frameRef.contentWindow?.postMessage({ type: "RESET_VIEW" }, "*");
        frameRef.contentWindow?.postMessage({ type: "SET_DARK_MODE", enabled: isDarkMode }, "*");
      }, 500);
    };
    frameRef.addEventListener("load", onLoad);
    return () => {
      frameRef.removeEventListener("load", onLoad);
    };
  }, [frameRef, syncResumeToArtboard, isDarkMode]);

  useEffect(() => {
    if (!frameRef?.contentWindow) return;
    frameRef.contentWindow.postMessage({ type: "SET_DARK_MODE", enabled: isDarkMode }, "*");
  }, [frameRef, isDarkMode]);

  useEffect(syncResumeToArtboard, [syncResumeToArtboard]);

  return (
    <iframe
      ref={setFrameRef}
      src={fit === "width" ? `${ARTBOARD_SRC}?fit=width` : ARTBOARD_SRC}
      title={t`Resume preview`}
      className="h-full w-full border-0"
    />
  );
};

// ──────────────────────────────────────────────────────────
// Section form wiring components
// ──────────────────────────────────────────────────────────

type SectionItemGroup = { items: unknown[]; name: string; visible: boolean; locked: boolean };

const PictureUploadSlot = () => {
  const inputRef = React.useRef<HTMLInputElement>(null);
  const { toast } = useToast();
  const { uploadImage, loading } = useUploadImage();
  const setValue = useResumeStore((s) => s.setValue);
  const picture = useResumeStore((s) => s.resume.data.basics.picture);

  const isValidUrl = useMemo(() => z.string().url().safeParse(picture.url).success, [picture.url]);

  const onSelectImage = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      const file = event.target.files[0];
      try {
        const response = await uploadImage(file);
        const url = response.data;
        setValue("basics.picture.url", url);
      } catch {
        toast({
          variant: "error",
          title: t`Failed to upload image.`,
          description: t`Please try again. If the problem persists, contact support.`,
        });
      }
    }
  };

  const onAvatarClick = () => {
    if (isValidUrl) {
      setValue("basics.picture.url", "");
    } else {
      inputRef.current?.click();
    }
  };

  return (
    <div className="group relative h-20 w-20 cursor-pointer rounded-lg" onClick={onAvatarClick}>
      {isValidUrl ? (
        <img src={picture.url} alt="" className="h-full w-full rounded-lg object-cover" />
      ) : (
        <div className="flex h-full w-full items-center justify-center rounded-lg border-2 border-dashed border-border bg-secondary/50">
          <UploadSimpleIcon size={24} weight="bold" className="text-muted-foreground" />
        </div>
      )}

      {isValidUrl && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center rounded-lg bg-background/30 opacity-0 transition-opacity group-hover:opacity-100">
          <TrashIcon size={16} weight="bold" className="text-foreground" />
        </div>
      )}

      {!isValidUrl && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center rounded-lg bg-background/30 opacity-0 transition-opacity group-hover:opacity-100">
          <UploadSimpleIcon size={16} weight="bold" className="text-foreground" />
        </div>
      )}

      <input
        ref={inputRef}
        hidden
        type="file"
        accept="image/*"
        disabled={loading}
        aria-label={t`Upload profile photo`}
        onChange={onSelectImage}
      />
    </div>
  );
};

const BasicsSection = () => {
  const setValue = useResumeStore((s) => s.setValue);
  const basics = useResumeStore((s) => s.resume.data.basics);
  const profiles = useResumeStore(
    (s) => (get(s.resume.data.sections, "profiles") as SectionItemGroup | undefined)?.items ?? [],
  );

  const value: BasicsValue = useMemo(
    () => ({
      name: basics.name,
      headline: basics.headline,
      email: basics.email,
      phone: basics.phone,
      location: basics.location,
      url: basics.url.href,
    }),
    [basics],
  );

  const socials: SocialLink[] = useMemo(
    () =>
      (profiles as { id: string; network: string; url: { href: string } }[]).map((p) => ({
        id: p.id,
        platform: p.network,
        url: p.url.href,
      })),
    [profiles],
  );

  const handleChange = useCallback(
    (patch: Partial<BasicsValue>) => {
      for (const [key, val] of Object.entries(patch)) {
        if (key === "url") {
          setValue("basics.url.href", val);
        } else {
          setValue(`basics.${key}`, val);
        }
      }
    },
    [setValue],
  );

  const handleAddSocial = useCallback(() => {
    const items = [
      ...(profiles as Record<string, unknown>[]),
      { ...defaultProfile, id: createId() },
    ];
    setValue("sections.profiles.items", items);
  }, [profiles, setValue]);

  const handleChangeSocial = useCallback(
    (id: string, patch: Partial<SocialLink>) => {
      const items = (profiles as Record<string, unknown>[]).map((p) => {
        if ((p as { id: string }).id !== id) return p;
        const updated = { ...p };
        if (patch.platform !== undefined) {
          updated.network = patch.platform;
          updated.icon = patch.platform;
        }
        if (patch.url !== undefined) {
          updated.url = { ...(p.url as { href: string; label: string }), href: patch.url };
        }
        return updated;
      });
      setValue("sections.profiles.items", items);
    },
    [profiles, setValue],
  );

  const handleRemoveSocial = useCallback(
    (id: string) => {
      const items = (profiles as { id: string }[]).filter((p) => p.id !== id);
      setValue("sections.profiles.items", items);
    },
    [profiles, setValue],
  );

  return (
    <BasicsForm
      value={value}
      socials={socials}
      platformOptions={PLATFORM_OPTIONS}
      pictureSlot={<PictureUploadSlot />}
      onChange={handleChange}
      onAddSocial={handleAddSocial}
      onChangeSocial={handleChangeSocial}
      onRemoveSocial={handleRemoveSocial}
    />
  );
};

const EntrySectionForm = ({ sectionKey }: { sectionKey: string }) => {
  const setValue = useResumeStore((s) => s.setValue);
  const section = useResumeStore(
    (s) => get(s.resume.data.sections, sectionKey) as SectionItemGroup | undefined,
  );
  const [openEntryId, setOpenEntryId] = useState<string | null>(null);

  const fieldMap = getSectionFieldMap(sectionKey);
  if (!section || !fieldMap) return null;

  const items = section.items as Record<string, unknown>[];

  const entries: ExperienceEntry[] = items.map((item) => {
    const dateStr = (item.date as string | undefined) ?? "";
    const { startDate, endDate, current } = parseDateString(dateStr);
    const itemUrl = item.url as { label: string; href: string } | undefined;
    return {
      id: item.id as string,
      title: (item[fieldMap.title] as string | undefined) ?? "",
      subtitle: (item[fieldMap.subtitle] as string | undefined) ?? "",
      location: fieldMap.location
        ? ((item[fieldMap.location] as string | undefined) ?? "")
        : undefined,
      startDate,
      endDate,
      current,
      url: itemUrl,
    };
  });

  const handleChangeEntry = (id: string, patch: Partial<ExperienceEntry>) => {
    const newItems = items.map((item) => {
      if ((item.id as string) !== id) return item;
      const updated = { ...item };

      if (patch.title !== undefined) {
        updated[fieldMap.title] = patch.title;
      }
      if (patch.subtitle !== undefined) {
        updated[fieldMap.subtitle] = patch.subtitle;
      }
      if (fieldMap.location && patch.location !== undefined) {
        updated[fieldMap.location] = patch.location;
      }
      if (patch.url !== undefined) {
        updated.url = patch.url;
      }

      if (
        patch.startDate !== undefined ||
        patch.endDate !== undefined ||
        patch.current !== undefined
      ) {
        const existingDate = (updated.date as string | undefined) ?? "";
        const existing = parseDateString(existingDate);
        const start = patch.startDate ?? existing.startDate;
        const end = patch.endDate ?? existing.endDate;
        const cur = patch.current ?? existing.current;
        updated.date = buildDateString(start, end, cur);
      }

      return updated;
    });
    setValue(`sections.${sectionKey}.items`, newItems);
  };

  const handleAddEntry = () => {
    const newItem = { ...defaultExperience, id: createId() };
    if (fieldMap.title !== "position") {
      (newItem as Record<string, unknown>)[fieldMap.title] = "";
    }
    if (fieldMap.subtitle !== "company") {
      (newItem as Record<string, unknown>)[fieldMap.subtitle] = "";
    }
    setValue(`sections.${sectionKey}.items`, [...items, newItem]);
    setOpenEntryId(newItem.id);
  };

  const handleRemoveEntry = (id: string) => {
    setValue(
      `sections.${sectionKey}.items`,
      items.filter((item) => (item.id as string) !== id),
    );
  };

  const handleToggleEntry = (id: string) => {
    setOpenEntryId((prev) => (prev === id ? null : id));
  };

  const handleReorderEntries = (reorderedEntries: ExperienceEntry[]) => {
    const reorderedItems = reorderedEntries.map((entry) => {
      const item = items.find((i) => (i.id as string) === entry.id);
      if (!item) return item;
      const updated = { ...item };
      updated[fieldMap.title] = entry.title;
      updated[fieldMap.subtitle] = entry.subtitle;
      if (fieldMap.location) {
        updated[fieldMap.location] = entry.location ?? "";
      }
      if (entry.url) {
        updated.url = entry.url;
      }
      updated.date = buildDateString(entry.startDate, entry.endDate, !!entry.current);
      return updated;
    });
    setValue(`sections.${sectionKey}.items`, reorderedItems);
  };

  const renderDescription = (entryId: string) => {
    const item = items.find((i) => (i.id as string) === entryId);
    if (!item) return null;
    return (
      <RichInput
        content={(item.summary as string | undefined) ?? ""}
        hideToolbar={false}
        onChange={(value) => {
          const newItems = items.map((i) =>
            (i.id as string) === entryId ? { ...i, summary: value } : i,
          );
          setValue(`sections.${sectionKey}.items`, newItems);
        }}
      />
    );
  };

  return (
    <ExperienceForm
      entries={entries}
      openEntryId={openEntryId}
      renderDescription={renderDescription}
      labels={{
        title: fieldMap.titleLabel,
        subtitle: fieldMap.subtitleLabel,
        addEntry: fieldMap.addLabel,
      }}
      onChangeEntry={handleChangeEntry}
      onAddEntry={handleAddEntry}
      onRemoveEntry={handleRemoveEntry}
      onToggleEntry={handleToggleEntry}
      onReorderEntries={handleReorderEntries}
    />
  );
};

const SkillSectionForm = ({ sectionKey }: { sectionKey: string }) => {
  const setValue = useResumeStore((s) => s.setValue);
  const section = useResumeStore(
    (s) => get(s.resume.data.sections, sectionKey) as SectionItemGroup | undefined,
  );
  const [draftValue, setDraftValue] = useState("");

  if (!section) return null;

  const items = section.items as Record<string, unknown>[];
  const showLevels = sectionKey === "skills" || sectionKey === "languages";

  const skills: DesignSkill[] = items.map((item) => ({
    id: item.id as string,
    name: (item.name as string | undefined) ?? "",
    level: showLevels
      ? LEVEL_SCHEMA_TO_DESIGN[Math.min((item.level as number | undefined) ?? 1, 5)]
      : undefined,
  }));

  const handleAddSkill = () => {
    if (!draftValue.trim()) return;
    const newItem = { ...defaultSkill, id: createId(), name: draftValue.trim() };
    setValue(`sections.${sectionKey}.items`, [...items, newItem]);
    setDraftValue("");
  };

  const handleRemoveSkill = (id: string) => {
    setValue(
      `sections.${sectionKey}.items`,
      items.filter((item) => (item.id as string) !== id),
    );
  };

  const handleChangeLevel = (id: string, level: number) => {
    const schemaLevel = LEVEL_DESIGN_TO_SCHEMA[level] ?? 1;
    const newItems = items.map((item) =>
      (item.id as string) === id ? { ...item, level: schemaLevel } : item,
    );
    setValue(`sections.${sectionKey}.items`, newItems);
  };

  const handleReorderSkills = (reorderedSkills: DesignSkill[]) => {
    const reorderedItems = reorderedSkills.map((skill) => {
      const item = items.find((i) => (i.id as string) === skill.id);
      return item ?? { id: skill.id, name: skill.name };
    });
    setValue(`sections.${sectionKey}.items`, reorderedItems);
  };

  let placeholder = t`Add a skill and press Enter`;
  if (sectionKey === "languages") placeholder = t`Add a language and press Enter`;
  if (sectionKey === "interests") placeholder = t`Add an interest and press Enter`;

  return (
    <SkillsForm
      skills={skills}
      draftValue={draftValue}
      showLevels={showLevels}
      placeholder={placeholder}
      onDraftChange={setDraftValue}
      onAddSkill={handleAddSkill}
      onRemoveSkill={handleRemoveSkill}
      onChangeLevel={handleChangeLevel}
      onReorderSkills={handleReorderSkills}
    />
  );
};

// Common Phosphor icons offered for custom section headings (editor affordance only).
const CUSTOM_SECTION_ICONS = [
  "star",
  "heart",
  "trophy",
  "medal",
  "certificate",
  "book-open",
  "code",
  "globe",
  "briefcase",
  "graduation-cap",
  "lightbulb",
  "wrench",
];

// Spec 046: custom sections get a name, an editor icon, and a Normal/Skill type toggle (FlowCV
// parity). Normal → entry list (EntrySectionForm). Skill → keyword list (SkillsForm UI), but the
// write handler creates FULL custom items so they satisfy customSectionSchema (only `name` shown).
const CustomSectionForm = ({ sectionKey }: { sectionKey: string }) => {
  const setValue = useResumeStore((s) => s.setValue);
  const section = useResumeStore(
    (s) =>
      get(s.resume.data.sections, sectionKey) as
        | (SectionItemGroup & { type?: "normal" | "skill"; icon?: string })
        | undefined,
  );
  const [draftValue, setDraftValue] = useState("");

  if (!section) return null;

  const type = section.type ?? "normal";
  const icon = section.icon ?? "";
  const items = section.items as Record<string, unknown>[];

  const setType = (next: "normal" | "skill") => {
    setValue(`sections.${sectionKey}.type`, next);
    // Skill lists read best in a multi-column grid; entries in a single column.
    setValue(`sections.${sectionKey}.columns`, next === "skill" ? 3 : 1);
  };

  const skills: DesignSkill[] = items.map((item) => ({
    id: item.id as string,
    name: (item.name as string | undefined) ?? "",
  }));
  const handleAddSkill = () => {
    if (!draftValue.trim()) return;
    const newItem = { ...defaultCustomSection, id: createId(), name: draftValue.trim() };
    setValue(`sections.${sectionKey}.items`, [...items, newItem]);
    setDraftValue("");
  };
  const handleRemoveSkill = (id: string) => {
    setValue(
      `sections.${sectionKey}.items`,
      items.filter((item) => (item.id as string) !== id),
    );
  };
  const handleReorderSkills = (reordered: DesignSkill[]) => {
    const reorderedItems = reordered
      .map((skill) => items.find((item) => (item.id as string) === skill.id))
      .filter((item): item is Record<string, unknown> => item !== undefined);
    setValue(`sections.${sectionKey}.items`, reorderedItems);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-end gap-2">
        <div className="space-y-1.5">
          <span className="block text-xs font-medium text-muted-foreground">{t`Icon`}</span>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                size="icon"
                variant="outline"
                className="h-10 w-10"
                aria-label={t`Section icon`}
              >
                <i className={`ph ph-bold ph-${icon || "squares-four"}`} />
              </Button>
            </PopoverTrigger>
            <PopoverContent align="start" side="bottom" className="w-56 space-y-2 p-2">
              <div className="grid grid-cols-6 gap-1">
                {CUSTOM_SECTION_ICONS.map((name) => (
                  <Button
                    key={name}
                    size="icon"
                    variant={icon === name ? "secondary" : "ghost"}
                    aria-label={name}
                    onClick={() => {
                      setValue(`sections.${sectionKey}.icon`, name);
                    }}
                  >
                    <i className={`ph ph-bold ph-${name}`} />
                  </Button>
                ))}
              </div>
              <Input
                value={icon}
                placeholder={t`Phosphor icon name`}
                onChange={(event) => {
                  setValue(`sections.${sectionKey}.icon`, event.target.value);
                }}
              />
            </PopoverContent>
          </Popover>
        </div>

        <div className="flex-1 space-y-1.5">
          <label
            htmlFor={`${sectionKey}-name`}
            className="block text-xs font-medium text-muted-foreground"
          >
            {t`Section name`}
          </label>
          <Input
            id={`${sectionKey}-name`}
            value={section.name}
            placeholder={t`e.g. Volunteering, Publications`}
            onChange={(event) => {
              setValue(`sections.${sectionKey}.name`, event.target.value);
            }}
          />
        </div>
      </div>

      <div className="space-y-1.5">
        <span className="block text-xs font-medium text-muted-foreground">{t`Type`}</span>
        <div className="grid grid-cols-2 gap-2">
          <Button
            variant={type === "normal" ? "secondary" : "outline"}
            aria-pressed={type === "normal"}
            onClick={() => {
              setType("normal");
            }}
          >
            {t`Normal`}
          </Button>
          <Button
            variant={type === "skill" ? "secondary" : "outline"}
            aria-pressed={type === "skill"}
            onClick={() => {
              setType("skill");
            }}
          >
            {t`Skill`}
          </Button>
        </div>
      </div>

      {type === "skill" ? (
        <SkillsForm
          skills={skills}
          draftValue={draftValue}
          showLevels={false}
          placeholder={t`Add an item and press Enter`}
          onDraftChange={setDraftValue}
          onAddSkill={handleAddSkill}
          onRemoveSkill={handleRemoveSkill}
          onReorderSkills={handleReorderSkills}
        />
      ) : (
        <EntrySectionForm sectionKey={sectionKey} />
      )}
    </div>
  );
};

const SummarySectionForm = () => {
  const setValue = useResumeStore((s) => s.setValue);
  const content = useResumeStore((s) => {
    const section = get(s.resume.data.sections, "summary") as { content?: string } | undefined;
    return section?.content ?? "";
  });

  const wordCount = useMemo(() => {
    const text = content.replace(/<[^>]*>/g, "").trim();
    return text ? text.split(/\s+/).length : 0;
  }, [content]);

  const charCount = useMemo(() => content.replace(/<[^>]*>/g, "").length, [content]);

  return (
    <SummaryForm
      richTextSlot={
        <RichInput
          content={content}
          hideToolbar={false}
          onChange={(value) => {
            setValue("sections.summary.content", value);
          }}
        />
      }
      wordCount={wordCount}
      charCount={charCount}
      hint={t`Aim for 40–60 words`}
    />
  );
};

// ──────────────────────────────────────────────────────────
// Section form dispatcher
// ──────────────────────────────────────────────────────────

const SectionFormContent = ({ sectionKey }: { sectionKey: string }) => {
  if (sectionKey === "basics") return <BasicsSection />;
  if (sectionKey === "summary") return <SummarySectionForm />;
  if (sectionKey === "profiles") return null;
  if (SKILL_SECTIONS.has(sectionKey)) return <SkillSectionForm sectionKey={sectionKey} />;
  if (ENTRY_SECTIONS.has(sectionKey)) return <EntrySectionForm sectionKey={sectionKey} />;
  if (sectionKey.startsWith("custom.")) return <CustomSectionForm sectionKey={sectionKey} />;
  return null;
};

// ──────────────────────────────────────────────────────────
// SortableSectionBlock — wraps SectionBlock for drag-to-reorder
// ──────────────────────────────────────────────────────────

type SectionData = {
  id: string;
  name: string;
  icon: React.ReactNode;
  count?: number;
  visible: boolean;
  locked: boolean;
};

type SortableSectionBlockProps = {
  section: SectionData;
  index: number;
  sectionDataLength: number;
  collapsedSections: Record<string, boolean | undefined>;
  onToggleCollapse: (id: string) => void;
  onToggleVisibility: (id: string) => void;
  onToggleLock: (id: string) => void;
  onMoveUp: (id: string, direction: -1 | 1) => void;
  onMoveDown: (id: string, direction: -1 | 1) => void;
  onDelete?: (id: string) => void;
  renderContent: (id: string) => React.ReactNode;
};

const SortableSectionBlock = ({
  section,
  index,
  sectionDataLength,
  collapsedSections,
  onToggleCollapse,
  onToggleVisibility,
  onToggleLock,
  onMoveUp,
  onMoveDown,
  onDelete,
  renderContent,
}: SortableSectionBlockProps) => {
  const { setNodeRef, transform, transition, attributes, listeners, isDragging } = useSortable({
    id: section.id,
  });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    opacity: isDragging ? 0.5 : undefined,
    transition,
  };

  return (
    <div ref={setNodeRef} style={style} id={`section-block-${section.id}`}>
      <SectionBlock
        title={section.name}
        icon={section.icon}
        count={section.count}
        visible={section.visible}
        locked={section.locked}
        collapsed={collapsedSections[section.id] ?? false}
        canMoveDown={index < sectionDataLength - 1}
        canMoveUp={index > 0}
        dragHandleProps={{ ...listeners, ...attributes }}
        onToggleCollapse={() => {
          onToggleCollapse(section.id);
        }}
        onToggleVisibility={() => {
          onToggleVisibility(section.id);
        }}
        onDelete={
          onDelete
            ? () => {
                onDelete(section.id);
              }
            : undefined
        }
        onMoveDown={() => {
          onMoveDown(section.id, 1);
        }}
        onMoveUp={() => {
          onMoveUp(section.id, -1);
        }}
        onToggleLock={() => {
          onToggleLock(section.id);
        }}
      >
        {renderContent(section.id)}
      </SectionBlock>
    </div>
  );
};

// ──────────────────────────────────────────────────────────
// ContentEditor — the Content-mode left column
// ──────────────────────────────────────────────────────────

const ContentEditor = () => {
  const resume = useResumeStore((state) => state.resume);
  const setValue = useResumeStore((state) => state.setValue);
  const addSection = useResumeStore((state) => state.addSection);
  const collapsedSections = useResumeStore((state) => state.collapsedSections);
  const toggleCollapseSection = useResumeStore((state) => state.toggleCollapseSection);
  const toggleSectionLock = useResumeStore((state) => state.toggleSectionLock);

  const sectionKeys = useMemo(
    () => resume.data.metadata.layout.flatMap((page) => page.flat()),
    [resume.data.metadata.layout],
  );

  const sectionData = useMemo(
    () =>
      sectionKeys.map((key) => {
        const section = get(resume.data.sections, key) as
          | (Section & { items?: unknown[]; icon?: string })
          | undefined;
        const customIcon = key.startsWith("custom.") ? section?.icon : undefined;
        return {
          id: key,
          name: section?.name ?? key,
          icon: customIcon ? (
            <i className={`ph ph-bold ph-${customIcon}`} />
          ) : (
            (SECTION_ICONS[key] ?? <StackIcon size={18} />)
          ),
          count: section?.items?.length,
          visible: section?.visible ?? true,
          locked: section?.locked ?? false,
        };
      }),
    [sectionKeys, resume.data.sections],
  );

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleToggleVisibility = useCallback(
    (sectionKey: string) => {
      const section = get(resume.data.sections, sectionKey) as Section | undefined;
      if (section) {
        setValue(`sections.${sectionKey}.visible`, !section.visible);
      }
    },
    [resume.data.sections, setValue],
  );

  const handleMoveSection = useCallback(
    (sectionKey: string, direction: -1 | 1) => {
      const layout: string[][][] = JSON.parse(JSON.stringify(resume.data.metadata.layout));
      for (const page of layout) {
        for (const col of page) {
          const idx = col.indexOf(sectionKey);
          if (idx === -1) continue;
          const newIdx = idx + direction;
          if (newIdx < 0 || newIdx >= col.length) return;
          [col[idx], col[newIdx]] = [col[newIdx], col[idx]];
          setValue("metadata.layout", layout);
          return;
        }
      }
    },
    [resume.data.metadata.layout, setValue],
  );

  const handleDragEndSection = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) return;

    const oldIndex = sectionData.findIndex((s) => s.id === active.id);
    const newIndex = sectionData.findIndex((s) => s.id === over.id);

    if (oldIndex === -1 || newIndex === -1) return;

    handleMoveSection(active.id as string, newIndex > oldIndex ? 1 : -1);
  };

  return (
    <div className="flex h-full min-h-0 flex-col bg-background">
      <div className="min-h-0 flex-1 overflow-y-auto">
        <div className="mx-auto w-full max-w-[680px] px-6 pb-24 pt-6">
          <header className="mb-5">
            <p className="font-mono text-[11px] text-primary">{t`Content`}</p>
            <h1 className="mt-1 text-2xl font-semibold tracking-tight text-foreground">{t`Write your resume`}</h1>
            <p className="mt-1.5 max-w-[46ch] text-sm leading-relaxed text-muted-foreground">
              {t`One section at a time. Changes save as you type and show in the preview.`}
            </p>
          </header>

          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            modifiers={[restrictToParentElement]}
            onDragEnd={handleDragEndSection}
          >
            <div className="flex flex-col gap-2.5">
              {/* Basics — always first, no move/visibility/lock */}
              <SectionBlock
                title={t`Basics`}
                icon={SECTION_ICONS.basics}
                collapsed={collapsedSections.basics ?? false}
                onToggleCollapse={() => {
                  toggleCollapseSection("basics");
                }}
              >
                <SectionFormContent sectionKey="basics" />
              </SectionBlock>

              {/* All other sections, in layout order */}
              <SortableContext
                items={sectionData.map((s) => s.id)}
                strategy={verticalListSortingStrategy}
              >
                {sectionData.map((section, index) => (
                  <SortableSectionBlock
                    key={section.id}
                    section={section}
                    index={index}
                    sectionDataLength={sectionData.length}
                    collapsedSections={collapsedSections}
                    renderContent={(sectionKey) => {
                      return <SectionFormContent sectionKey={sectionKey} />;
                    }}
                    onDelete={
                      section.id.startsWith("custom.")
                        ? (id) => {
                            // Guarded by section.id.startsWith("custom.") above, so this is a valid custom SectionKey
                            useResumeStore.getState().removeSection(id as SectionKey);
                          }
                        : undefined
                    }
                    onMoveDown={(id, direction) => {
                      handleMoveSection(id, direction);
                    }}
                    onMoveUp={(id, direction) => {
                      handleMoveSection(id, direction);
                    }}
                    onToggleCollapse={toggleCollapseSection}
                    onToggleLock={toggleSectionLock}
                    onToggleVisibility={handleToggleVisibility}
                  />
                ))}
              </SortableContext>
            </div>
          </DndContext>

          <div className="mt-3">
            <AddSectionCard onAdd={addSection} />
          </div>
        </div>
      </div>
    </div>
  );
};

// ──────────────────────────────────────────────────────────
// Main page
// ──────────────────────────────────────────────────────────

export const BuilderRedesignPage = () => {
  const { isDesktop } = useBreakpoint();
  const resume = useResumeStore((state) => state.resume);
  const { isDarkMode, setTheme } = useTheme();
  const { data: me } = useMe();
  const pastStates = useTemporalResumeStore((s) => s.pastStates);
  const futureStates = useTemporalResumeStore((s) => s.futureStates);
  const { status, retry, flush } = useAutoSave(resume);
  useBlockedSaveNavigation(status, flush);

  // Feature hooks
  const settings = useBuilderSettings();
  const zoomPan = useZoomPan();
  const { exportStatus, handleDownload, handleDownloadAts } = useExportActions();

  // UI state
  const [mode, setMode] = useState<BuilderMode>("content");
  const [templatesOpen, setTemplatesOpen] = useState(false);

  const title = resume.title;

  // ── Derived data ──

  const avatar = useMemo(() => {
    if (me?.picture) {
      // eslint-disable-next-line lingui/no-unlocalized-strings
      return <img src={me.picture} alt="" className="h-full w-full rounded-full object-cover" />;
    }
    return me?.name.charAt(0).toUpperCase() ?? "U";
  }, [me]);

  // ── Handlers ──

  const handleUndo = useCallback(() => {
    useResumeStore.temporal.getState().undo();
  }, []);

  const handleRedo = useCallback(() => {
    useResumeStore.temporal.getState().redo();
  }, []);

  const menuItems = useMemo(
    () =>
      defaultBuilderMenuItems({
        onBrowseTemplates: () => {
          setTemplatesOpen(true);
        },
        onExportJSON: () => {
          void handleDownload("json");
        },
        onDownloadAtsPdf: () => {
          void handleDownloadAts();
        },
      }),
    [handleDownload, handleDownloadAts],
  );

  // ── Render ──

  return (
    <>
      <Helmet>
        <title>
          {title} — {BRANDING.name}
        </title>
        <meta name="robots" content="noindex,follow" />
      </Helmet>

      {!isDesktop && (
        <MobileReview
          previewSlot={<ArtboardFrame fit="width" />}
          resumeTitle={title}
          saveStatus={status}
          exportStatus={exportStatus}
          onRetry={retry}
          onExportClick={() => {
            void handleDownload("pdf");
          }}
        />
      )}

      {isDesktop && (
        <div className="flex h-screen flex-col overflow-hidden" data-testid="builder-root">
          <BuilderTopbar
            resumeName={title}
            mode={mode}
            canRedo={futureStates.length > 0}
            canUndo={pastStates.length > 0}
            isDark={isDarkMode}
            menuItems={menuItems}
            avatar={avatar}
            onModeChange={setMode}
            onUndo={handleUndo}
            onRedo={handleRedo}
            onToggleTheme={() => {
              setTheme(isDarkMode ? "light" : "dark");
            }}
            onDownload={() => {
              void handleDownload("pdf");
            }}
          />

          <BuilderShell
            leftSlot={
              mode === "content" ? (
                <ContentEditor />
              ) : (
                <Suspense>
                  <SettingsPanel
                    themeProps={settings.themeProps}
                    typographyProps={settings.typographyProps}
                    pageProps={settings.pageProps}
                    layoutProps={settings.layoutProps}
                    spacingProps={settings.spacingProps}
                    headingsProps={settings.headingsProps}
                    linksProps={settings.linksProps}
                    headerProps={settings.headerProps}
                    templates={settings.settingsTemplates}
                    activeTemplateId={settings.currentTemplate}
                    onSelectTemplate={settings.handleSelectTemplate}
                    onProTemplate={settings.handleProTemplate}
                  />
                </Suspense>
              )
            }
            rightSlot={
              <RightPanel
                isFullscreen={zoomPan.fullscreen}
                previewSlot={<ArtboardFrame />}
                statusSlot={<SaveBadge status={status} onRetry={retry} />}
                zoom={zoomPan.zoom}
                onCenter={zoomPan.center}
                onFit={zoomPan.zoomFit}
                onToggleFullscreen={zoomPan.toggleFullscreen}
                onZoomIn={zoomPan.zoomIn}
                onZoomOut={zoomPan.zoomOut}
              />
            }
          />
        </div>
      )}

      {/* Template Switcher Modal */}
      <Suspense>
        {templatesOpen && (
          <TemplateSwitcherModal
            open={templatesOpen}
            templates={settings.templateItems}
            activeId={settings.currentTemplate}
            onClose={() => {
              setTemplatesOpen(false);
            }}
            onSelect={(id) => {
              settings.handleSelectTemplate(id);
              setTemplatesOpen(false);
            }}
            onProSelect={settings.handleProTemplate}
          />
        )}
      </Suspense>
    </>
  );
};

export const builderRedesignLoader: LoaderFunction<ResumeDto> = async ({ params }) => {
  try {
    if (!params.id) return redirect("/dashboard/resumes");
    const id = params.id;

    const resume = await queryClient.fetchQuery({
      queryKey: ["resume", { id }],
      queryFn: () => findResumeById({ id }),
    });

    resume.data.metadata.layout = resume.data.metadata.layout.map((page) => [
      page[0] ?? [],
      page[1] ?? [],
    ]);

    useResumeStore.setState({ resume });
    useResumeStore.temporal.getState().clear();

    return resume;
  } catch {
    return redirect("/dashboard/resumes");
  }
};
